import { useEffect, useState } from "react";
import { C } from "../lib/theme";
import { Icon } from "./Icon";
import { supabase } from "../lib/supabase";

// Three-step permanent account deletion. Mirrors mobile's DeleteAccountModal
// (features/profile/components/DeleteAccountModal.tsx) so both clients gate
// on the same check_deletion_eligibility RPC and collect closure_payout_phone
// before calling the delete-account Edge Function.
//
// Flow:
//   0. Open — check_deletion_eligibility(p_user_id) → if blocked, show blockers.
//   1. Warning screen (extra seller warning if seller account).
//   2. Type "DELETE" confirmation.
//   3. Payout phone (for any future refunds) + current password.
//      → signInWithPassword to re-authenticate, then POST to delete-account
//        with Bearer token + closure_payout_phone in body.
//      → on success, supabase.auth.signOut() returns us to the Login screen.

export function DeleteAccountModal({ open, onClose, user, onDeleted }) {
  const [step, setStep] = useState(1);
  const [deleteText, setDeleteText] = useState("");
  const [password, setPassword] = useState("");
  const [closurePayoutPhone, setClosurePayoutPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [blockers, setBlockers] = useState([]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setDeleteText("");
      setPassword("");
      setClosurePayoutPhone("");
      setError("");
      setLoading(false);
      setEligibility(null);
      setBlockers([]);
      setIsSeller(false);
      return;
    }
    if (!user?.id) return;
    let cancelled = false;
    setEligibility("checking");
    (async () => {
      const [{ data: sellerRow }, { data: profile }, { data: eligible }] = await Promise.all([
        supabase.from("sellers").select("id").eq("user_id", user.id).maybeSingle(),
        supabase.from("users").select("phone").eq("id", user.id).maybeSingle(),
        supabase.rpc("check_deletion_eligibility", { p_user_id: user.id }),
      ]);
      if (cancelled) return;
      setIsSeller(!!sellerRow);
      if (profile?.phone && !profile.phone.startsWith("deleted-")) {
        setClosurePayoutPhone(profile.phone);
      }
      if (eligible && !eligible.eligible) {
        const msgs = [];
        if (eligible.open_orders > 0) msgs.push(`${eligible.open_orders} open order${eligible.open_orders > 1 ? "s" : ""} — complete or cancel them first`);
        if (eligible.disputed_escrow > 0) msgs.push(`${eligible.disputed_escrow} disputed payment${eligible.disputed_escrow > 1 ? "s" : ""} — wait for resolution`);
        if (eligible.seller_active_orders > 0) msgs.push(`${eligible.seller_active_orders} active seller order${eligible.seller_active_orders > 1 ? "s" : ""} — fulfil or cancel them first`);
        if (eligible.pending_escrow > 0) msgs.push(`${eligible.pending_escrow} escrow payment${eligible.pending_escrow > 1 ? "s" : ""} still held — wait for release`);
        setBlockers(msgs);
        setEligibility("blocked");
      } else {
        setEligibility("eligible");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user?.id]);

  async function handleFinalDelete() {
    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }
    if (!closurePayoutPhone.trim()) {
      setError("Please enter a phone number for any pending refunds.");
      return;
    }
    setLoading(true);
    setError("");

    // reauth — no login lockout; user is already authenticated.
    // Re-authenticate with password to confirm ownership. signInWithPassword
    // refreshes the session even if one exists; if the password is wrong we
    // get a clean error without mutating auth state.
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: user?.email ?? "",
      password,
    });
    if (signInErr) {
      setError("Incorrect password");
      setLoading(false);
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (!session) {
      setError("Session expired. Please log in again.");
      setLoading(false);
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ closure_payout_phone: closurePayoutPhone.trim() }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Eligibility changed between open-check and submit — bounce back to
        // the blocked view so the user sees the new blockers.
        if (res.status === 409 && Array.isArray(result.blockers) && result.blockers.length) {
          setBlockers(result.blockers);
          setEligibility("blocked");
          setStep(1);
          setLoading(false);
          return;
        }
        setError(result.error || "Failed to delete account. Please try again.");
        setLoading(false);
        return;
      }
      // Auth row is gone; signing out clears local storage + session cookies.
      await supabase.auth.signOut();
      try {
        localStorage.clear();
      } catch { /* storage may be unavailable */ }
      setLoading(false);
      onDeleted?.();
      onClose?.();
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          background: C.white,
          borderRadius: 20,
          boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
          padding: 24,
        }}
      >
        {eligibility !== "checking" && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <button
              onClick={onClose}
              style={{ all: "unset", cursor: "pointer", padding: 4 }}
              disabled={loading}
            >
              <Icon name="x" size={18} color={C.muted} />
            </button>
          </div>
        )}

        {eligibility === "eligible" && (
          <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: 16 }}>
            {[1, 2, 3].map((s) => (
              <span
                key={s}
                style={{
                  width: s === step ? 24 : 8,
                  height: 4,
                  borderRadius: 2,
                  background: s === step ? C.red : `${C.dustDk}60`,
                }}
              />
            ))}
          </div>
        )}

        {(eligibility === "checking" || eligibility === null) && <CheckingView />}
        {eligibility === "blocked" && <BlockedView blockers={blockers} onClose={onClose} />}
        {eligibility === "eligible" && step === 1 && (
          <Step1Warning isSeller={isSeller} onContinue={() => setStep(2)} onCancel={onClose} />
        )}
        {eligibility === "eligible" && step === 2 && (
          <Step2Confirm
            deleteText={deleteText}
            setDeleteText={setDeleteText}
            onBack={() => {
              setStep(1);
              setDeleteText("");
            }}
            onNext={() => setStep(3)}
          />
        )}
        {eligibility === "eligible" && step === 3 && (
          <Step3Password
            closurePayoutPhone={closurePayoutPhone}
            setClosurePayoutPhone={setClosurePayoutPhone}
            password={password}
            setPassword={(v) => {
              setPassword(v);
              if (error) setError("");
            }}
            error={error}
            loading={loading}
            onBack={() => {
              setStep(2);
              setPassword("");
              setError("");
            }}
            onDelete={handleFinalDelete}
          />
        )}
      </div>
    </div>
  );
}

function CheckingView() {
  return (
    <div style={{ padding: "32px 0", textAlign: "center" }}>
      <div
        style={{
          width: 40,
          height: 40,
          border: `3px solid ${C.dustDk}40`,
          borderTopColor: C.hunter,
          borderRadius: "50%",
          margin: "0 auto 16px",
          animation: "kapio-spin 0.8s linear infinite",
        }}
      />
      <div style={{ fontSize: 13, color: C.muted }}>Checking account status…</div>
      <style>{`@keyframes kapio-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function BlockedView({ blockers, onClose }) {
  return (
    <>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: C.amberBg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="alert-triangle" size={24} color={C.amber} />
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.charcoal, textAlign: "center", marginBottom: 8 }}>
        Cannot delete account yet
      </div>
      <div style={{ fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 16, lineHeight: 1.5 }}>
        Please resolve the following before deleting your account:
      </div>
      <div style={{ background: C.amberBg, borderRadius: 12, padding: 14, marginBottom: 20, display: "grid", gap: 8 }}>
        {blockers.map((b, i) => (
          <div key={i} style={{ fontSize: 13, color: C.amberDk, lineHeight: 1.45 }}>• {b}</div>
        ))}
      </div>
      <button
        onClick={onClose}
        style={{
          all: "unset",
          width: "100%",
          boxSizing: "border-box",
          cursor: "pointer",
          padding: "14px 16px",
          borderRadius: 14,
          background: C.cream,
          color: C.charcoal,
          fontSize: 14,
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        OK, got it
      </button>
    </>
  );
}

function Step1Warning({ isSeller, onContinue, onCancel }) {
  return (
    <>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: C.redBg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="alert-triangle" size={24} color={C.red} />
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.charcoal, textAlign: "center", marginBottom: 12 }}>
        Delete your account?
      </div>
      <div style={{ background: C.redBg, borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.charcoal, lineHeight: 1.5 }}>
          This action is permanent and cannot be undone. Deleting your account will:
        </div>
        <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
          {[
            "Remove all your personal data",
            "Delete your order history",
            "Remove all saved items and preferences",
          ].map((item, i) => (
            <div key={i} style={{ fontSize: 13, color: C.charcoal, lineHeight: 1.5 }}>• {item}</div>
          ))}
        </div>
      </div>
      {isSeller && (
        <div
          style={{
            background: "#FEF9C3",
            borderRadius: 12,
            padding: 14,
            marginBottom: 16,
            display: "flex",
            gap: 10,
          }}
        >
          <Icon name="store" size={16} color={C.amberDk} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.amberDk, marginBottom: 2 }}>
              Seller account warning
            </div>
            <div style={{ fontSize: 12, color: C.amberDk, lineHeight: 1.45 }}>
              Your store and all listed products will be permanently removed. Any pending payouts will be
              forfeited unless released to the payout phone on the next step.
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "grid", gap: 8 }}>
        <button
          onClick={onContinue}
          style={{
            all: "unset",
            cursor: "pointer",
            padding: "14px 16px",
            borderRadius: 14,
            background: C.red,
            color: C.white,
            fontSize: 14,
            fontWeight: 800,
            textAlign: "center",
          }}
        >
          Continue with deletion
        </button>
        <button onClick={onCancel} style={secondaryBtnStyle}>
          Cancel
        </button>
      </div>
    </>
  );
}

function Step2Confirm({ deleteText, setDeleteText, onBack, onNext }) {
  const match = deleteText === "DELETE";
  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.charcoal, marginBottom: 8 }}>
        Confirm deletion
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.45 }}>
        Type <span style={{ fontWeight: 800, color: C.red }}>DELETE</span> below to confirm you want to
        permanently delete your account.
      </div>
      <input
        autoFocus
        value={deleteText}
        onChange={(e) => setDeleteText(e.target.value.toUpperCase())}
        placeholder='Type "DELETE"'
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 14px",
          fontSize: 14,
          letterSpacing: 2,
          textAlign: "center",
          background: C.cream,
          border: `1.5px solid ${match ? C.red : C.dustDk}`,
          borderRadius: 14,
          color: C.charcoal,
          outline: "none",
          marginBottom: 16,
          fontFamily: "inherit",
        }}
      />
      <div style={{ display: "grid", gap: 8 }}>
        <button
          onClick={onNext}
          disabled={!match}
          style={{
            all: "unset",
            cursor: match ? "pointer" : "not-allowed",
            padding: "14px 16px",
            borderRadius: 14,
            background: match ? C.red : `${C.red}40`,
            color: C.white,
            fontSize: 14,
            fontWeight: 800,
            textAlign: "center",
          }}
        >
          Next
        </button>
        <button onClick={onBack} style={secondaryBtnStyle}>
          Back
        </button>
      </div>
    </>
  );
}

function Step3Password({
  closurePayoutPhone,
  setClosurePayoutPhone,
  password,
  setPassword,
  error,
  loading,
  onBack,
  onDelete,
}) {
  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.charcoal, marginBottom: 8 }}>
        Final confirmation
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.45 }}>
        Your account will be permanently deleted. Any future refunds will be sent to the number below.
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: C.charcoal, marginBottom: 6 }}>
        Payout phone for any pending refunds
      </div>
      <input
        type="tel"
        value={closurePayoutPhone}
        onChange={(e) => setClosurePayoutPhone(e.target.value)}
        placeholder="+260 9XX XXX XXX"
        style={textInputStyle(false)}
      />

      <div style={{ fontSize: 12, fontWeight: 700, color: C.charcoal, marginBottom: 6, marginTop: 14 }}>
        Your password
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          borderRadius: 14,
          background: C.cream,
          border: `1.5px solid ${error ? C.red : C.dustDk}`,
          marginBottom: error ? 8 : 16,
        }}
      >
        <Icon name="lock" size={16} color={C.muted} />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 14,
            color: C.charcoal,
            fontFamily: "inherit",
          }}
        />
      </div>
      {error && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 12, fontWeight: 600 }}>{error}</div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        <button
          onClick={onDelete}
          disabled={loading || !password.trim()}
          style={{
            all: "unset",
            cursor: loading || !password.trim() ? "not-allowed" : "pointer",
            padding: "14px 16px",
            borderRadius: 14,
            background: loading || !password.trim() ? `${C.red}60` : C.red,
            color: C.white,
            fontSize: 14,
            fontWeight: 800,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {loading ? (
            <span style={{ fontSize: 13, fontWeight: 700 }}>Deleting…</span>
          ) : (
            <>
              <Icon name="trash-2" size={14} color={C.white} />
              <span>Delete my account</span>
            </>
          )}
        </button>
        <button onClick={onBack} style={secondaryBtnStyle} disabled={loading}>
          Back
        </button>
      </div>
    </>
  );
}

const secondaryBtnStyle = {
  all: "unset",
  cursor: "pointer",
  padding: "14px 16px",
  borderRadius: 14,
  background: C.cream,
  color: C.charcoal,
  fontSize: 14,
  fontWeight: 700,
  textAlign: "center",
  boxSizing: "border-box",
};

function textInputStyle(error) {
  return {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    fontSize: 14,
    background: C.cream,
    border: `1.5px solid ${error ? C.red : C.dustDk}`,
    borderRadius: 14,
    color: C.charcoal,
    outline: "none",
    fontFamily: "inherit",
  };
}
